import BioscopeLine from '@/assets/icons/bioscope_line.svg?component';
import type { ConfirmRegistrationItem } from '@/components/organisms/FishGroupForms';

import type { Translate } from './penOptions';
import type { SitePen } from './sitePens';

/**
 * Where a bioscope is after an action. Measurements follow the pen: a bioscope measures
 * whatever group is in its pen, so one left in a pen without fish measures none, and one
 * moved into a pen that already holds another measures that pen's group beside it.
 */
export type BioscopeOutcome =
    /** `moved`: a move's destination bioscope, which measures the same group, not a new one. */
    | { kind: 'measures'; ids: number[]; pen: string; moved?: boolean }
    | { kind: 'movesIn'; ids: number[]; from: string; pen: string }
    | { kind: 'staysBehind'; ids: number[]; pen: string }
    | { kind: 'none'; pen: string };

function without(ids: readonly number[], removed: readonly number[]): number[] {
    return ids.filter((id) => !removed.includes(id));
}

function penOutcomes(
    pen: SitePen,
    own: readonly number[],
    moved: { ids: readonly number[]; from: string } | null
): BioscopeOutcome[] {
    const outcomes: BioscopeOutcome[] = [];
    if (own.length > 0) outcomes.push({ kind: 'measures', ids: [...own], pen: pen.displayName });
    if (moved && moved.ids.length > 0) {
        outcomes.push({
            kind: 'movesIn',
            ids: [...moved.ids],
            from: moved.from,
            pen: pen.displayName,
        });
    }
    if (outcomes.length === 0) outcomes.push({ kind: 'none', pen: pen.displayName });
    return outcomes;
}

/**
 * Every bioscope a split touches: those in each new group's pen (the group's own included when
 * it keeps its pen), the ones it moves, and the ones left in its pen when no new group stays.
 */
export function splitBioscopeOutcomes(args: {
    pens: readonly SitePen[];
    sourceCageId: number | null;
    destinationCageIds: readonly number[];
    /** The group's bioscopes that move, and where to; none when they stay. */
    moving: { ids: readonly number[]; toCageId: number } | null;
}): BioscopeOutcome[] {
    const { pens, sourceCageId, destinationCageIds, moving } = args;
    const source = pens.find((pen) => pen.cageId === sourceCageId);
    const movedIds = moving?.ids ?? [];
    const outcomes = [...new Set(destinationCageIds)].flatMap((cageId) => {
        const pen = pens.find((candidate) => candidate.cageId === cageId);
        if (!pen) return [];
        const own = cageId === sourceCageId ? without(pen.bioscopeIds, movedIds) : pen.bioscopeIds;
        const moved =
            moving && moving.toCageId === cageId
                ? { ids: moving.ids, from: source?.displayName ?? '' }
                : null;
        return penOutcomes(pen, own, moved);
    });
    if (source && sourceCageId !== null && !destinationCageIds.includes(sourceCageId)) {
        const left = without(source.bioscopeIds, movedIds);
        if (left.length > 0) {
            outcomes.push({ kind: 'staysBehind', ids: left, pen: source.displayName });
        }
    }
    return outcomes;
}

/**
 * Every bioscope a merge touches: the destination pen's own, the one moved in beside them,
 * and the ones left in the source pens the merge empties.
 */
export function mergeBioscopeOutcomes(args: {
    destination: SitePen | undefined;
    sourcePens: readonly SitePen[];
    /** A source pen's bioscopes that move into the destination; none when all stay. */
    moving: { ids: readonly number[]; fromCageId: number } | null;
}): BioscopeOutcome[] {
    const { destination, sourcePens, moving } = args;
    if (!destination) return [];
    const from = sourcePens.find((pen) => pen.cageId === moving?.fromCageId);
    const outcomes = penOutcomes(
        destination,
        destination.bioscopeIds,
        moving && from ? { ids: moving.ids, from: from.displayName } : null
    );
    for (const pen of sourcePens) {
        if (pen.cageId === destination.cageId) continue;
        const left =
            pen.cageId === moving?.fromCageId
                ? without(pen.bioscopeIds, moving.ids)
                : pen.bioscopeIds;
        if (left.length > 0)
            outcomes.push({ kind: 'staysBehind', ids: left, pen: pen.displayName });
    }
    return outcomes;
}

/** One Confirm card per outcome, so the customer sees every bioscope before confirming. */
export function bioscopeOutcomeItems(
    outcomes: readonly BioscopeOutcome[],
    t: Translate
): ConfirmRegistrationItem[] {
    return outcomes.map((outcome) => {
        const key = `bioscope-${outcome.kind}-${outcome.pen}`;
        if (outcome.kind === 'none') {
            return {
                key,
                icon: BioscopeLine,
                title: t('board.row.noBioscope'),
                text: t('siteActions.confirm.noBioscopeIn', { pen: outcome.pen }),
            };
        }
        const title = t('siteActions.bioscopeIds', { ids: outcome.ids.join(', ') });
        const count = outcome.ids.length;
        const text =
            outcome.kind === 'measures'
                ? outcome.moved
                    ? t('siteActions.confirm.bioscopeMeasuresMoved', { count, pen: outcome.pen })
                    : t('siteActions.confirm.bioscopeMeasures', { count, pen: outcome.pen })
                : outcome.kind === 'movesIn'
                  ? t('siteActions.confirm.bioscopeMovesIn', {
                        count,
                        from: outcome.from,
                        pen: outcome.pen,
                    })
                  : t('siteActions.confirm.bioscopeStaysBehind', { count, pen: outcome.pen });
        return { key, icon: BioscopeLine, title, text };
    });
}
